import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { EnvelopeIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import AuthActionShell from '../components/Auth/AuthActionShell';
import { Alert, Button, Card, Input, Spinner } from '../components/common';
import { BRAND_LOGO_FULL_SRC } from '../config/brandAssets';
import { authService } from '../services/authService';
import {
  decodePostAuthIntentFromQuery,
  savePostAuthIntentFromQueryValue,
} from '../utils/postAuthIntent';
import ResetPasswordAction from './ResetPasswordAction';

const removeSensitiveResetParams = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const url = new URL(window.location.href);
  ['oobCode', 'apiKey', 'continueUrl'].forEach((key) => url.searchParams.delete(key));
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
};

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const actionMode = String(searchParams.get('mode') || '').trim();
  const oobCode = String(searchParams.get('oobCode') || '').trim();
  const prefilledEmail = String(searchParams.get('email') || '').trim();
  const queryIntentParam = String(searchParams.get('postAuthIntent') || '').trim();
  const isUnsupportedActionMode = Boolean(actionMode && actionMode !== 'resetPassword');
  const isPasswordChangeFlow =
    !isUnsupportedActionMode &&
    oobCode.length > 0 &&
    (!actionMode || actionMode === 'resetPassword');
  const intentPath = decodePostAuthIntentFromQuery(queryIntentParam);

  const [email, setEmail] = useState(prefilledEmail);
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestStatus, setRequestStatus] = useState<{
    type: 'success' | 'warning' | 'error';
    message: string;
  } | null>(null);

  useEffect(() => {
    if (!queryIntentParam) {
      return;
    }
    savePostAuthIntentFromQueryValue(queryIntentParam, 'reset-password-query');
  }, [queryIntentParam]);

  const requestButtonLabel = useMemo(() => {
    return requestLoading ? 'Wysyłanie...' : 'Wyślij link resetu hasła';
  }, [requestLoading]);
  const loginTarget = queryIntentParam ? `/login?postAuthIntent=${queryIntentParam}` : '/login';

  const handleRequestReset = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setRequestStatus(null);
    setRequestLoading(true);
    try {
      const result = await authService.requestPasswordReset(email);
      if (result.status === 'sent') {
        setRequestStatus({ type: 'success', message: result.userMessage });
      } else if (result.status === 'user-not-found') {
        setRequestStatus({ type: 'warning', message: result.userMessage });
      } else {
        setRequestStatus({ type: 'error', message: result.userMessage });
      }
    } finally {
      setRequestLoading(false);
    }
  };

  if (isUnsupportedActionMode) {
    return (
      <AuthActionShell
        heading="Link konta"
        description="Ten typ linku nie jest obsługiwany w tym widoku."
        statusProps={{ role: 'alert' }}
      >
        <Link
          to={loginTarget}
          className="flex min-h-[46px] w-full items-center justify-center rounded-lg bg-ratuj-400 px-5 py-2.5 text-base font-semibold text-white shadow-button transition-colors hover:bg-ratuj-300 focus-outline"
        >
          Wróć do logowania
        </Link>
      </AuthActionShell>
    );
  }

  if (isPasswordChangeFlow) {
    return (
      <ResetPasswordAction
        oobCode={oobCode}
        intentPath={intentPath}
        onTerminalResult={removeSensitiveResetParams}
      />
    );
  }

  return (
    <main className="flex min-h-screen bg-primary-dark-100 px-4 py-6 sm:px-6 sm:py-10">
      <div className="mx-auto flex w-full max-w-md items-center">
        <Card className="w-full p-6 sm:p-8">
          <Link
            to="/"
            className="mb-5 inline-flex min-h-11 items-center rounded-lg focus-outline"
            aria-label="Ratuj Sprzęt — strona główna"
          >
            <img src={BRAND_LOGO_FULL_SRC} alt="" className="h-11 w-auto" />
          </Link>
          <h1 className="text-2xl font-bold text-primary-light-100">Reset hasła</h1>
          <p className="mt-3 text-sm leading-6 text-primary-light-300">
            Podaj email, a wyślemy link do resetu hasła.
          </p>

          <form onSubmit={handleRequestReset} className="mt-6 space-y-5">
            {requestStatus && (
              <div role={requestStatus.type === 'error' ? 'alert' : 'status'}>
                <Alert
                  variant={
                    requestStatus.type === 'success'
                      ? 'success'
                      : requestStatus.type === 'warning'
                        ? 'warning'
                        : 'error'
                  }
                  icon={<ExclamationCircleIcon className="h-5 w-5" />}
                >
                  {requestStatus.message}
                </Alert>
              </div>
            )}

            <Input
              id="resetEmail"
              type="email"
              label="Adres email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              required
              icon={<EnvelopeIcon className="h-5 w-5 text-primary-light-400/80" />}
              placeholder="twoj@email.pl"
              disabled={requestLoading}
            />

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              disabled={requestLoading || !email.trim()}
            >
              {requestLoading ? (
                <>
                  <Spinner size="sm" color="white" className="mr-2" />
                  {requestButtonLabel}
                </>
              ) : (
                requestButtonLabel
              )}
            </Button>

            <Link
              to={loginTarget}
              className="flex min-h-11 items-center justify-center rounded-lg text-sm text-ratuj-300 hover:text-ratuj-200 focus-outline"
            >
              Wróć do logowania
            </Link>
          </form>
        </Card>
      </div>
    </main>
  );
};

export default ResetPasswordPage;
